import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { IconAlertTriangle, IconArrowLeft, IconDownload, IconPrinter } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useFirestore } from "../../helpers/firebaseContext";
import { renderGenevaReportTemplate } from "./reportTemplates";
import type { ReportDocument, ReportEntryDocument } from "./reportTemplates/types";
import "./GenevaReportPrintPage.css";

type GenevaReportPrintViewProps = {
  reportId?: string | null;
  onBack?: () => void;
};

const extractReportWarnings = (
  report: ReportDocument | null,
  entries: ReportEntryDocument[],
): string[] => {
  const columns = report?.result?.columns || [];
  if (columns.length === 0 || entries.length === 0) {
    return [];
  }

  const warningIndexes = columns
    .map((columnName, index) => ({ columnName, index }))
    .filter(({ columnName }) => /(^|_)(error|warning)(message)?$/i.test(columnName) || /ErrorMessage/i.test(columnName));

  if (warningIndexes.length === 0) {
    return [];
  }

  const warnings = new Set<string>();

  entries.forEach((entryDoc) => {
    const entry = entryDoc.entry || [];
    warningIndexes.forEach(({ index }) => {
      const value = entry[index];
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed) {
          warnings.add(trimmed);
        }
      }
    });
  });

  return Array.from(warnings);
};

export function GenevaReportPrintView({ reportId, onBack }: GenevaReportPrintViewProps) {
  const firestore = useFirestore();

  const [report, setReport] = useState<ReportDocument | null>(null);
  const [entries, setEntries] = useState<ReportEntryDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const handleBack = onBack ?? (() => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.close();
  });

  const reportWarnings = useMemo(() => extractReportWarnings(report, entries), [entries, report]);

  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return undefined;
    }

    const reportRef = doc(firestore, "reports", reportId);
    const entriesQuery = query(collection(firestore, "reports", reportId, "entries"), orderBy("index"));

    let reportReady = false;
    let entriesReady = false;

    const maybeFinishLoading = () => {
      if (reportReady && entriesReady) {
        setLoading(false);
      }
    };

    const unsubscribeReport = onSnapshot(reportRef, (snapshot) => {
      setReport(snapshot.exists() ? snapshot.data() as ReportDocument : null);
      reportReady = true;
      maybeFinishLoading();
    });

    const unsubscribeEntries = onSnapshot(entriesQuery, (snapshot) => {
      setEntries(snapshot.docs.map((entryDoc) => entryDoc.data() as ReportEntryDocument));
      entriesReady = true;
      maybeFinishLoading();
    });

    return () => {
      unsubscribeReport();
      unsubscribeEntries();
    };
  }, [firestore, reportId]);

  if (loading) {
    return (
      <div className="report-print-view">
        <div className="statement-loading">
          <Group mb="md">
            <Loader size="sm" />
            <Text fw={600}>Loading Geneva report</Text>
          </Group>
          <p>Fetching the report document and print rows from Firestore.</p>
        </div>
      </div>
    );
  }

  if (!reportId || !report) {
    return (
      <div className="report-print-view">
        <div className="statement-error">
          <h1>Report not found</h1>
          <p>The report document could not be loaded.</p>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  if (report.status === "error") {
    return (
      <div className="report-print-view">
        <div className="statement-error">
          <h1>Report failed</h1>
          <p>{report.message || "Geneva returned an error while generating this report."}</p>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  if (report.status !== "completed") {
    return (
      <div className="report-print-view">
        <div className="statement-loading">
          <Group mb="md">
            <Loader size="sm" />
            <Text fw={600}>Preparing Geneva report</Text>
          </Group>
          <p>{report.message || "The report is still being generated. This page will update automatically when it is ready."}</p>
          {report.stage && (
            <p>Current stage: {report.stage}</p>
          )}
          <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-print-view">
      <div className="report-print-toolbar">
        <div className="report-print-toolbar-card">
          <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            Back
          </Button>
          <Button variant="light" leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>
            Print
          </Button>
          <Button leftSection={<IconDownload size={16} />} onClick={() => window.print()}>
            Save as PDF
          </Button>
          <Stack gap={0}>
            <Text size="sm" fw={700}>{report.reportTitle || report.reportName}</Text>
            <Text size="xs" c="dimmed">Report ID: {reportId}</Text>
          </Stack>
        </div>
      </div>

      {reportWarnings.length > 0 && (
        <div className="report-print-warning">
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Geneva returned report warnings"
          >
            <Stack gap={4}>
              {reportWarnings.map((warning) => (
                <Text key={warning} size="sm">{warning}</Text>
              ))}
            </Stack>
          </Alert>
        </div>
      )}

      {renderGenevaReportTemplate({ report, entries })}
    </div>
  );
}

export default function GenevaReportPrintPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();

  return (
    <GenevaReportPrintView
      reportId={reportId}
      onBack={() => navigate(-1)}
    />
  );
}
